import React from "react";
import get from "lodash/get";
import {Box, Button, Column, Columns, Split, Stack, TextBlock} from "@unibuddy/patron";
import styled from "styled-components";
import {useTranslation} from "@unibuddy/intl";
import {compose} from "redux";
import withAnalytics from "shared/AnalyticsNew/withAnalytics/withAnalytics";
import withAuth from "shared/Auth/withAuth/withAuth";
import BlogStatistics from "./BlogStatistics";
import MentorFlags from "../../../../../shared/Mentor/components/MentorFlags/MentorFlags";
import BlogPostDetailMedia from "../../../../../shared/Blog/components/BlogPostDetailMedia/BlogPostDetailMedia";
import BlogPostTextPreview from "../../../../../shared/Blog/components/BlogPostTextPreview/BlogPostTextPreview";
import ThemedLink from "../../../../../shared/Navigation/components/ThemedLink/ThemedLink";
import {getenv} from "../../../../../util/getEnv";
import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_VIEW_CONTENT} from "../../../../../translations/blogs";

const Avatar = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 100%;
    border: 2px solid white;

    @media (width >= 768px) {
        width: 90px;
        height: 90px;
    }
`;

const StyledTextBlock = styled(TextBlock)`
    paddingbottom: 10px;
`;

const StyledBox = styled(Box)`
    border-radius: 8px 8px 0px 0px;
`;

const mediaMetrics = {
    height: 190,
    width: 340,
};

const ContentPreview = (props) => {
    const title: string = props.post.title;
    const authorDegree = get(props, "post.author.degree.name");
    const PREVIEW_TEXT_LENGTH = 230 - title?.length;
    const {t} = useTranslation([IntlNamespaces.BLOGS]);
    const authorName: string = get(props, "post.author.firstName");
    const id: string = props.post.id;
    return (
        <Box
            shadow={"medium"}
            width={["240px", mediaMetrics.width]}
            backgroundColor={"white"}
            position={"relative"}
            borderRadius={"xsmall"}
        >
            <StyledBox
                height={mediaMetrics.height}
                width={["240px", mediaMetrics.width]}
                bgColor={"black"}
            >
                <BlogPostDetailMedia
                    aspectRatio={"none"}
                    post={props.post}
                    height={mediaMetrics.height}
                    width={mediaMetrics.width}
                    borderRadius={"5px 5px 0 0"}
                />
            </StyledBox>
            <Box
                padding={"small"}
                marginBottom={"small"}
                aria-label={`${title}, Post written by ${authorName}`}
                width={"100%"}
            >
                <Stack space={"xsmall"}>
                    <Box top={"150px"} position={"absolute"}>
                        <Columns verticalAlign={"bottom"}>
                            <Column width={"1/3"}>
                                <Avatar
                                    id={`blog-header-avatar-${id}`}
                                    alt={`${authorName}'s Avatar`}
                                    src={
                                        get(props, "post.author.profilePhoto", false) ||
                                        getenv("DEFAULT_PROFILE_PHOTO")
                                    }
                                />
                                <MentorFlags
                                    countryCode={get(props, "post.author.country.code", null)}
                                    primaryFlag={get(
                                        props,
                                        "post.author.mentorFields.primaryFlag.code",
                                        null,
                                    )}
                                    secondaryFlag={get(
                                        props,
                                        "post.author.mentorFields.secondaryFlag.code",
                                        null,
                                    )}
                                />
                            </Column>
                            <Column>
                                <Split space={"small"}>
                                    <Box paddingLeft={"medium"}>
                                        <Stack space={"small"}>
                                            <StyledTextBlock size={"small"} weight={"700"}>
                                                {`${authorName}`}
                                            </StyledTextBlock>
                                            {authorDegree && (
                                                <TextBlock truncate size={"xsmall"} weight={"400"}>
                                                    {authorDegree}
                                                </TextBlock>
                                            )}
                                        </Stack>
                                    </Box>
                                </Split>
                            </Column>
                        </Columns>
                    </Box>
                    <Box padding={"xsmall"} marginTop={"xlarge"}>
                        <TextBlock size={"large"} weight={"700"} lineHeight={"medium"}>
                            {title}
                        </TextBlock>
                    </Box>
                    <Box padding={"xsmall"} minH={50}>
                        <BlogPostTextPreview
                            color={"grey"}
                            body={get(props, "post.body")}
                            length={PREVIEW_TEXT_LENGTH}
                        />
                    </Box>
                    <Box paddingX={"xsmall"}>
                        <BlogStatistics
                            created={get(props, "post.created")}
                            numberOfViews={get(props, "post.numberOfViews")}
                            readingTime={get(props, "post.readingTime")}
                            color={"grey"}
                        />
                    </Box>
                    <ThemedLink
                        to={get(props, "blogDetailLink")}
                        external={false}
                        onClick={() => {
                            props.analytics.events.viewPostViaActions({
                                ambassadorId: get(props, "ambassadorId"),
                                prospectId: get(props, "authState.me.anyUser.id"),
                                sharedURL: get(props, "shareUrl"),
                                shareType: "CONTENT",
                            });
                        }}
                    >
                        <Button full ghost round color="university">
                            {t(INTL_VIEW_CONTENT.key, INTL_VIEW_CONTENT.defaultValue)}
                        </Button>
                    </ThemedLink>
                </Stack>
            </Box>
        </Box>
    );
};

export default compose(withAnalytics, withAuth)(ContentPreview);
